import { FormInstance } from 'antd'
import { useEffect, useState } from 'react'
import { useAnalyzeJobOffer } from '../../../prompts/hooks'
import { AnalyzeJobOfferResult } from '../../../prompts/output/analyze-job-offer-result'
import { AgentType } from '../../agent-type'
import { DEFAULT_CAMPAIGN_LOCALE } from '../../campaign-locale'
import { StoreInput } from '../../input/store-input'
import { getCampaignAgentConfig } from './campaign-agent-config'

type UseJobOfferAnalysisProps = {
    form: FormInstance<StoreInput>
    agentConfig?: ReturnType<typeof getCampaignAgentConfig>
    selectedType?: AgentType
    locale: string
}

// Generates interview questions from the job posting and tracks which language they were made in.
export function useJobOfferAnalysis({ form, agentConfig, selectedType, locale }: UseJobOfferAnalysisProps) {
    const [analyzeJobOfferResult, setAnalyzeJobOfferResult] = useState<AnalyzeJobOfferResult>()
    const [analyzedLocale, setAnalyzedLocale] = useState<string>()
    const [editingJobOffer, setEditingJobOffer] = useState(false)

    const analyzeJobOfferMutation = useAnalyzeJobOffer()

    useEffect(() => {
        setAnalyzeJobOfferResult(undefined)
        setAnalyzedLocale(undefined)
        setEditingJobOffer(false)
    }, [selectedType])

    const analyze = (feedback?: string): Promise<void> => {
        if (!agentConfig?.buildAnalyzeInput || !agentConfig.applyAnalyzeResult) {
            return Promise.resolve()
        }

        const applyAnalyzeResult = agentConfig.applyAnalyzeResult
        const input = { ...agentConfig.buildAnalyzeInput(form), feedback: feedback?.trim() || null }
        const type = selectedType

        return analyzeJobOfferMutation.mutateAsync(input).then((result) => {
            if (form.getFieldValue('type') !== type) {
                return
            }

            applyAnalyzeResult(form, result)
            setAnalyzeJobOfferResult(result)
            setAnalyzedLocale(input.locale ?? DEFAULT_CAMPAIGN_LOCALE)
            setEditingJobOffer(false)
        })
    }

    return {
        analyzeJobOfferResult,
        analyzedLocale,
        questionsNeedRegeneration: !!analyzeJobOfferResult && analyzedLocale !== locale,
        editingJobOffer,
        setEditingJobOffer,
        analyze,
        isAnalyzing: analyzeJobOfferMutation.isPending,
    }
}
